import { useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import { ErrorMessage } from '@hookform/error-message';
import { useForm } from 'react-hook-form';
import FormInput from '../FoodMenu/FormInput';
import {
  updateCategoryDetails,
  addNewCategory,
} from '../../../services/SettingsService';
import { objectToFormData } from '../../../utils/objectToFormData';
import uploadImageIcon from '../../../images/upload_image.png';

const notify = (id, type, message, time) => {
  toast.update(id, {
    render: message,
    type: type,
    position: 'top-center',
    autoClose: time,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    isLoading: false,
  });
};

const AddCategory = (props) => {
  const { addNewModal, setAddNewModal, categoryDetails, categoryId } = props;
  const uploadRef = useRef(null);

  const {
    handleSubmit,
    formState: { errors },
    register,
    watch,
    reset,
  } = useForm({
    mode: 'all',
    defaultValues: { name: '', status: true, img: null },
  });
  const { ref, ...rest } = register('img', { 
    validate: (value) => {
      // Only require image for new categories, not for updates
      if (!categoryId && (!value || value.length === 0)) {
        return 'Category image is required!';
      }
      return true;
    }
  });
  const watchImage = watch('img', null);
  const watchStatus = watch('status', true);

  // Remember single toast ID to prevent duplicates
  const toastId = useRef(null);

  useEffect(() => {
    if (addNewModal) {
      if (categoryId && categoryDetails) {
        const categoryData = Array.isArray(categoryDetails)
          ? categoryDetails[0]
          : categoryDetails;
        reset({
          name: categoryData?.name || '',
          status:
            categoryData?.status !== undefined ? categoryData.status : true,
          img: null,
        });
      } else {
        reset({ name: '', status: true, img: null });
      }
    }
  }, [addNewModal, categoryId, categoryDetails, reset]);

  const onSubmit = async (data) => {
    try {
      // Only create the loading toast if not active already to avoid duplicates
      if (!toast.isActive(toastId.current)) {
        toastId.current = toast.loading(categoryId ? 'Updating...' : 'Creating...');
      }

      if (categoryId && categoryDetails) {
        const updateData = { name: data.name, status: data.status };
        if (data.img && data.img.length > 0) updateData.img = data.img[0];
        const postData = objectToFormData(updateData, '');
        const res = await updateCategoryDetails(categoryId, postData);

        if (res.status === 'success') {
          notify(toastId.current, 'success', 'Category details updated!', 2000);
          setAddNewModal(false);
          toastId.current = null;
        } else {
          notify(toastId.current, 'error', res.data?.message || 'Update failed', 2000);
          toastId.current = null;
        }
      } else {
        const createData = { name: data.name, status: data.status, img: data.img[0] };
        const postData = objectToFormData(createData);
        const res = await addNewCategory(postData);

        if (res.status === 'success') {
          notify(toastId.current, 'success', 'New category details added!', 2000);
          setAddNewModal(false);
          toastId.current = null;
        } else {
          notify(toastId.current, 'error', res.data?.message || 'Creation failed', 2000);
          toastId.current = null;
        }
      }
    } catch (error) {
      if (toast.isActive(toastId.current)) {
        notify(toastId.current, 'error', 'An error occurred. Please try again.', 3000);
        toastId.current = null;
      } else {
        toast.error('An error occurred. Please try again.');
      }
    }
  };

  if (categoryId && !categoryDetails) {
    return (
      <div id='preloader-inner'>
        <div className='sk-three-bounce-inner'>
          <div className='sk-child sk-bounce1' />
          <div className='sk-child sk-bounce2' />
          <div className='sk-child sk-bounce3' />
          <div id='preloader-inner-bg' />
        </div>
      </div>
    );
  }

  return (
    <>
      <Modal
        show={addNewModal}
        onHide={() => setAddNewModal(false)}
        className='modal fade'
        id='addNewModal'
      >
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>
              {categoryDetails && categoryId ? 'Update Category' : 'Add Category'}
            </h5>
            <button
              type='button'
              className='close'
              data-dismiss='modal'
              onClick={() => setAddNewModal(false)}
            >
              <span>×</span>
            </button>
          </div>
          <div className='modal-body'>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className='food-image media mb-3 ava'>
                {(categoryDetails &&
                  (Array.isArray(categoryDetails)
                    ? categoryDetails[0]?.img
                    : categoryDetails.img)) ||
                (uploadRef?.current?.files && uploadRef.current.files.length > 0) ? (
                  <>
                    <button
                      type='button'
                      onClick={() => uploadRef.current.click()}
                      className='btn btn-primary food-image-edit'
                    >
                      <i className='flaticon-381-edit'></i>
                    </button>
                    <img
                      className='mr-3 img-fluid rounded'
                      src={
                        watchImage &&
                        watchImage.length > 0 &&
                        uploadRef.current?.files?.length > 0
                          ? URL.createObjectURL(uploadRef.current.files[0])
                          : process.env.REACT_APP_API_RESOURCE_URL +
                            (Array.isArray(categoryDetails)
                              ? categoryDetails[0]?.img
                              : categoryDetails?.img)
                      }
                      alt='upload category pic here'
                      style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                    />
                  </>
                ) : (
                  <img
                    className='mr-3 img-fluid rounded'
                    onClick={() => uploadRef?.current?.click()}
                    style={{ width: '100px', cursor: 'pointer' }}
                    src={uploadImageIcon}
                    alt='upload category pic here'
                  />
                )}
                <div className='custom-file mb-3'>
                  <input
                    type='file'
                    {...rest}
                    name='img'
                    ref={(e) => {
                      ref(e);
                      uploadRef.current = e; 
                    }}
                    className='custom-file-input'
                    id='val-img'
                    hidden
                    accept='image/*'
                  />
                </div>
              </div>
                <ErrorMessage
                  errors={errors}
                  name='img'
                  render={({ message }) => <div  className='text-danger fs-12 mb-3 ml-3'>{message}</div>}
                />
              <FormInput
                label={'Name'}
                id={'val-name'}
                name={'name'}
                required={true}
                ErrorMessage={ErrorMessage}
                register={{
                  ...register('name', { required: 'Category name is required!' }),
                }}
                errors={errors}
              />
              <div className='custom-control custom-checkbox mb-3'>
                <input
                  type='checkbox'
                  className='custom-control-input'
                  {...register('status')}
                  checked={watchStatus}
                  id='val-status'
                />
                <label className='custom-control-label' htmlFor='val-status'>
                  Active
                </label>
              </div>
              <button className='btn btn-primary btn-block'>
                {categoryId && categoryDetails ? 'Update' : 'Add'}
              </button>
            </form>
          </div>
        </div>
      </Modal>
      <ToastContainer />
    </>
  );
};

export default AddCategory;
